<template>
  <NiceModal :name="name" :title="$gettext('Capture photo')" @close="cancel" @submit="submit">
    <section class="camera-container">
      <!-- Camera input -->
      <NiceDropdown
        v-if="devices.length > 1"
        :title="$gettext('Camera input')"
        v-model="selectedDeviceId"
        :values="devices"
        :key-only="true"
        key-name="deviceId"
        value-name="label"
        @change="switchCamera"
      ></NiceDropdown>

      <!-- Camera preview -->
      <div id="camera-view" class="video-wrapper">
        <video ref="videoRef" autoplay playsinline muted></video>
      </div>

      <!-- Hidden canvas to capture image -->
      <canvas ref="canvasRef" style="display: none"></canvas>

      <!-- Loading -->
      <NiceLoading v-if="loading" fullDiv></NiceLoading>
    </section>

    <template #footer>
      <!-- Cancel -->
      <NiceButton :text="$gettext('Cancel')" @click="cancel" type="default" plain icon="icon-x" />

      <!-- Submit -->
      <NiceButton :text="$gettext('Capture')" @click="submit" :loading="loading" icon="icon-camera" />
    </template>
  </NiceModal>
</template>

<script setup>
import { defineEmits, inject, onMounted, onBeforeUnmount, ref } from "vue";

const nice = inject("nice");
const emit = defineEmits(["cancel", "success"]);
const name = "camera-modal";
const loading = ref(false);

const videoRef = ref(null);
const canvasRef = ref(null);
const devices = ref([]);
const selectedDeviceId = ref("");
let mediaStream = null;

/**
 * Initialize the camera.
 * We request permission first to ensure labels are available when enumerating devices.
 */
async function initCamera() {
  loading.value = true;
  try {
    // 1. Start default camera to get permissions
    await startStream();
    // 2. List available devices
    await getVideoInputDevices();
  } catch (err) {
    console.error("Camera initialization failed:", err);
    nice.toast("Failed to access camera", "error");
  } finally {
    loading.value = false;
  }
}

/**
 * Get list of video input devices
 */
async function getVideoInputDevices() {
  const allDevices = await navigator.mediaDevices.enumerateDevices();
  devices.value = allDevices.filter((d) => d.kind === "videoinput");

  // Set initial selected device if not already set
  if (!selectedDeviceId.value && devices.value.length > 0) {
    // Try to find the active track's device ID, otherwise default to first
    const currentTrack = mediaStream?.getVideoTracks()[0];
    const currentId = currentTrack?.getSettings()?.deviceId;
    selectedDeviceId.value = currentId || devices.value[0].deviceId;
  }
}

/**
 * Start the video stream
 */
async function startStream(deviceId = null) {
  stopStream(); // Stop any existing stream first

  const constraints = {
    audio: false,
    video: deviceId ? { deviceId: { exact: deviceId } } : true,
  };

  try {
    mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

    // Attach stream to video element
    if (videoRef.value) {
      videoRef.value.srcObject = mediaStream;
    }
  } catch (err) {
    console.error("Error starting stream:", err);
    throw err;
  }
}

/**
 * Stop the current video stream to release hardware
 */
function stopStream() {
  if (mediaStream) {
    mediaStream.getTracks().forEach((track) => track.stop());
    mediaStream = null;
  }
}

/**
 * Handle switching cameras from dropdown
 */
async function switchCamera() {
  loading.value = true;
  await startStream(selectedDeviceId.value);
  loading.value = false;
}

function close() {
  nice.modal(name, false);
}

function cancel() {
  emit("cancel");
  close();
}

async function submit() {
  if (!videoRef.value || !canvasRef.value) return;
  loading.value = true;

  try {
    const video = videoRef.value;
    const canvas = canvasRef.value;
    const context = canvas.getContext("2d");

    // Match canvas size to video resolution
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw the current video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to Blob
    canvas.toBlob(
      (blob) => {
        stopStream(); // Turn off camera
        emit("success", blob); // Send blob back to parent
        close();
      },
      "image/jpeg",
      0.8
    ); // Format and quality

    loading.value = false;
  } catch (error) {
    console.error("Capture failed", error);
    loading.value = false;
  }
}

// Modal open/close handler
function onToggle(data) {
  if (data.name === name) {
    if (data.isOpen) {
      initCamera();
    } else {
      stopStream();
    }
  }
}

onMounted(() => {
  nice.onModal(onToggle);
  // initCamera();
});

onBeforeUnmount(() => {
  stopStream();
});
</script>

<style lang="scss" scoped>
.camera-container {
  position: relative;
}

.video-wrapper {
  width: 100%;
  background: var(--nice-hover-color);
  border-radius: var(--nice-border-radius);
  border: 1px solid var(--nice-border-color);
  overflow: hidden;
  display: flex;
  justify-content: center;
}

video {
  width: 100%;
  height: 400px; /* Adjust based on your UI needs */
  object-fit: cover;
}

.camera-select {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}
</style>
